import React, { useEffect, useRef, useState } from "react";

/* ══════════════════════════════════════════════════════════════════════
   "Install as web app" button for the landing page.

   Behavior:
   - Hides entirely if the app is already running in standalone / PWA
     mode (window.matchMedia("(display-mode: standalone)") or iOS's
     navigator.standalone).
   - Listens for Chrome/Edge/Android's `beforeinstallprompt` event and
     stashes it. Clicking the button triggers the native install prompt.
   - On iOS Safari (no install API) or when the event hasn't fired yet,
     the click opens a modal with platform-specific "Add to Home Screen"
     instructions.
   - After a successful install the button removes itself.
   ══════════════════════════════════════════════════════════════════════ */

function detectPlatform(){
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (isIOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

function isStandalone(){
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator && window.navigator.standalone) return true; /* iOS Safari */
  return false;
}

export default function InstallAppButton({ lang = "en", variant = "gradient", label }){
  const isRTL = lang === "ar";
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [standalone, setStandalone] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const platformRef = useRef(detectPlatform());

  useEffect(() => {
    setStandalone(isStandalone());
    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (standalone || installed) return null;

  const handleClick = async () => {
    if (deferred) {
      try {
        await deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") setInstalled(true);
      } catch {
        /* swallow — user cancelled or browser refused */
      }
      setDeferred(null);
      return;
    }
    /* Fallback: show manual instructions */
    setShowModal(true);
  };

  const btnLabel = label || (isRTL ? "تثبيت كتطبيق ويب" : "Install as web app");

  const baseBtnStyle = {
    border: "none",
    borderRadius: 14,
    fontSize: 14,
    fontWeight: 600,
    padding: "12px 20px",
    cursor: "pointer",
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "transform 0.15s, box-shadow 0.15s, filter 0.15s",
  };

  const buttonStyle = variant === "ghost"
    ? { ...baseBtnStyle, background: "transparent", color: "var(--fg)", border: "1px solid var(--border-strong)" }
    : { ...baseBtnStyle, background: "var(--accent-grad)", color: "#fff", boxShadow: "0 6px 22px -4px color-mix(in srgb, var(--accent-a) 50%, transparent)" };

  return (
    <>
      <button type="button" onClick={handleClick} style={buttonStyle} aria-haspopup="dialog">
        <DownloadIcon />
        {btnLabel}
      </button>

      {showModal && (
        <InstallInstructionsModal
          platform={platformRef.current}
          lang={lang}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

function DownloadIcon(){
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12"/>
      <path d="M7 10l5 5 5-5"/>
      <path d="M5 21h14"/>
    </svg>
  );
}

function IosShareIcon(){
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12"/>
      <path d="M8 7l4-4 4 4"/>
      <rect x="4" y="13" width="16" height="8" rx="2"/>
    </svg>
  );
}

function IosAddIcon(){
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3"/>
      <path d="M12 8v8"/>
      <path d="M8 12h8"/>
    </svg>
  );
}

function AndroidMenuIcon(){
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="5" r="1"/>
      <circle cx="12" cy="12" r="1"/>
      <circle cx="12" cy="19" r="1"/>
    </svg>
  );
}

function InstallInstructionsModal({ platform, lang, onClose }){
  const isRTL = lang === "ar";

  const title = platform === "ios"
    ? (isRTL ? "إضافة إلى الشاشة الرئيسية" : "Add to Home Screen")
    : (isRTL ? "تثبيت التطبيق" : "Install the app");

  const steps = platform === "ios"
    ? [
        { icon: <IosShareIcon/>, text: isRTL ? "اضغط على زر المشاركة في شريط المتصفح" : "Tap the Share button in Safari's toolbar" },
        { icon: <IosAddIcon/>,   text: isRTL ? "اختر \"إضافة إلى الشاشة الرئيسية\"" : 'Select "Add to Home Screen"' },
        { icon: null,            text: isRTL ? "اضغط \"إضافة\" في الزاوية العلوية" : 'Tap "Add" in the top corner' },
      ]
    : platform === "android"
      ? [
          { icon: <AndroidMenuIcon/>, text: isRTL ? "افتح قائمة المتصفح (⋮)" : "Open your browser's menu (⋮)" },
          { icon: null,               text: isRTL ? "اختر \"تثبيت التطبيق\" أو \"إضافة إلى الشاشة الرئيسية\"" : 'Select "Install app" or "Add to Home Screen"' },
          { icon: null,               text: isRTL ? "اضغط \"تثبيت\"" : 'Tap "Install"' },
        ]
      : [
          { icon: null, text: isRTL ? "افتح قائمة المتصفح" : "Open your browser's menu" },
          { icon: null, text: isRTL ? "ابحث عن \"تثبيت التطبيق\" أو \"إضافة إلى الشاشة الرئيسية\"" : 'Look for "Install app" or "Add to Home Screen"' },
        ];

  const close = isRTL ? "إغلاق" : "Close";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cmd-install-title"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(10,10,11,0.72)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-elev)",
          color: "var(--fg)",
          border: "1px solid var(--border)",
          borderRadius: 18,
          maxWidth: 420,
          width: "100%",
          padding: 24,
          boxShadow: "var(--shadow-lg)",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Helvetica, Arial, sans-serif",
        }}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--accent-grad)", display: "grid", placeItems: "center", boxShadow: "0 4px 14px -3px color-mix(in srgb, var(--accent-a) 55%, transparent)" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/></svg>
          </div>
          <h3 id="cmd-install-title" style={{ fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: "-0.01em" }}>{title}</h3>
        </div>

        <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
          {steps.map((s, i) => (
            <li key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{
                width: 28, height: 28, borderRadius: 999,
                background: "color-mix(in srgb, var(--accent-a) 18%, transparent)",
                border: "1px solid color-mix(in srgb, var(--accent-a) 24%, transparent)",
                display: "grid", placeItems: "center",
                color: "var(--accent-a)", fontSize: 13, fontWeight: 700,
                flexShrink: 0,
              }}>{i + 1}</span>
              {s.icon && <span style={{ color: "var(--fg-muted)", flexShrink: 0 }}>{s.icon}</span>}
              <span style={{ fontSize: 14, color: "var(--fg)", lineHeight: 1.4 }}>{s.text}</span>
            </li>
          ))}
        </ol>

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 20,
            width: "100%",
            height: 40,
            border: "1px solid var(--border-strong)",
            borderRadius: 10,
            background: "transparent",
            color: "var(--fg)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {close}
        </button>
      </div>
    </div>
  );
}
